import React from 'react';
import { Card } from './ui/card';
import { CheckCircle, Award, Clock, DollarSign, HeadphonesIcon, Lock } from 'lucide-react';

export const WhyChooseUs = () => {
  const reasons = [
    {
      icon: Award,
      title: "95% Success Rate",
      description: "Our expert team has achieved one of the highest trademark approval rates in the industry, backed by years of USPTO experience and deep knowledge of examination procedures. We understand what examining attorneys look for and how to position your application for success. This exceptional success rate stems from our meticulous application preparation, comprehensive pre-filing searches, strategic classification advice, and skilled handling of office actions. When you work with us, you're not just filing paperwork—you're benefiting from proven strategies that consistently deliver registrations."
    },
    {
      icon: Clock,
      title: "Fast Turnaround",
      description: "We prioritize efficiency without sacrificing quality because we understand that time matters in business. Most applications are prepared, reviewed, and filed within 24-48 hours of receiving your information, allowing you to secure your filing date quickly. A trademark's priority date is critical—it determines who has superior rights if conflicts arise. Our streamlined processes, experienced staff, and efficient workflows mean you get professional service fast. We also respond promptly to all USPTO communications and handle office actions quickly to avoid unnecessary delays in your registration timeline."
    },
    {
      icon: DollarSign,
      title: "Transparent Pricing",
      description: "We believe in honest, upfront pricing with no hidden fees or surprise charges. Unlike some firms that advertise low prices but then add countless fees for basic services, our package prices are all-inclusive and clearly outlined from day one. You'll know exactly what you're paying for before you commit. Each pricing tier is designed to provide genuine value, with clear explanations of what's included and what's not. We also provide detailed invoices, explain all USPTO government fees separately from our professional fees, and never pressure you to buy services you don't need. This transparency builds trust and ensures you can budget confidently for your trademark protection."
    },
    {
      icon: HeadphonesIcon,
      title: "Dedicated Support",
      description: "From your first inquiry through final registration and beyond, you'll work with an assigned case manager who knows your file intimately and can answer questions without making you repeat your story. Your case manager provides personalized attention, proactive updates at key milestones, clear explanations of complex legal issues in plain English, and responsive communication via your preferred method (email, phone, or video call). You'll never feel lost in a queue or wonder about your application status. We're here to guide you through every step, celebrate milestones with you, and ensure you feel supported throughout the entire trademark registration journey."
    },
    {
      icon: Lock,
      title: "Comprehensive Protection",
      description: "We provide end-to-end trademark services that cover every aspect of brand protection, from initial availability searches and strategic filing advice through registration, ongoing monitoring, enforcement support, and maintenance filings. This holistic approach ensures your trademark is not only registered but actively protected against infringement for decades to come. We help you understand the full scope of protection available, including multi-class filings if you operate in different product or service categories, intent-to-use applications for planned expansions, and international protection strategies. Our goal is to build a comprehensive protection strategy that grows with your business."
    },
    {
      icon: CheckCircle,
      title: "USPTO Registered Attorneys",
      description: "Our team includes USPTO-registered trademark attorneys who have passed the specialized trademark examination and maintain active standing with the Office of Enrollment and Discipline. This means they're qualified to practice before the USPTO and have demonstrated mastery of federal trademark law, procedures, and ethics requirements. When complex legal issues arise—such as challenging office actions, responding to likelihood of confusion rejections, or handling opposition proceedings—you have experienced legal professionals advocating for your interests. Our attorneys stay current on evolving trademark law through continuing education and daily practice, ensuring you benefit from the most up-to-date expertise available."
    }
  ];

  return (
    <section className="py-20 bg-[#FFF7ED]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#7C2D12] mb-4">
              Why Choose Trademark Fashion Brand
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine legal expertise, proven results, and exceptional client service to deliver trademark protection you can trust.
            </p>
          </div>

          {/* Reasons Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {reasons.map((reason, index) => {
              const IconComponent = reason.icon;
              
              return (
                <Card 
                  key={index}
                  className="p-8 bg-white border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300 hover:shadow-xl group"
                >
                  <div className="bg-[#FFF7ED] w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#F97316] transition-colors duration-300">
                    <IconComponent className="text-[#F97316] group-hover:text-white transition-colors duration-300" size={32} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#7C2D12] mb-3">
                    {reason.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </Card>
              );
            })}
          </div>

          {/* Bottom Highlight Section */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 bg-white border-l-4 border-[#F97316] text-center">
              <div className="text-4xl font-bold text-[#7C2D12] mb-2">1000+</div>
              <div className="text-gray-600 font-medium">Successful Filings</div>
            </Card>
            
            <Card className="p-6 bg-white border-l-4 border-[#F97316] text-center">
              <div className="text-4xl font-bold text-[#7C2D12] mb-2">15+</div>
              <div className="text-gray-600 font-medium">Years of Experience</div>
            </Card>
            
            <Card className="p-6 bg-white border-l-4 border-[#F97316] text-center">
              <div className="text-4xl font-bold text-[#7C2D12] mb-2">500+</div>
              <div className="text-gray-600 font-medium">Satisfied Clients</div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
